// Audio utility functions for handling Supabase storage and audio file validation

import { supabaseUrl, bucketName, bucketName_files, backupBucketName } from "../components/env";
import { toSignedUrl } from "./signedUrls";

/**
 * File extensions we treat as valid audio in storage URLs (includes mp4: iOS/Safari
 * MediaRecorder uses audio/mp4 and filenames often end in .mp4, same container as m4a).
 */
const STORAGE_AUDIO_EXT_PATTERN = /\.(webm|mp3|wav|m4a|aac|ogg|mp4)$/i;

/**
 * Validates Supabase storage URL structure and accessibility
 * @param {string} audioUrl - The audio URL to validate
 * @returns {Object} - Validation result with status and details
 */
export const validateSupabaseStorageUrl = (audioUrl) => {
  if (!audioUrl || typeof audioUrl !== "string") {
    return {
      isValid: false,
      error: "No audio URL provided",
      details: null,
    };
  }

  // Check if it's a Supabase storage URL
  if (!audioUrl.includes("supabase.co/storage")) {
    return {
      isValid: true,
      error: null,
      details: { type: "external_url", url: audioUrl },
    };
  }

  // Parse Supabase storage URL (accept both public and signed URLs)
  const storagePattern =
    /supabase\.co\/storage\/v1\/object\/(?:public|sign)\/([^/?]+)\/(.+?)(?:\?|$)/;
  const match = audioUrl.match(storagePattern);

  if (!match) {
    return {
      isValid: false,
      error: "Invalid Supabase storage URL format",
      details: { url: audioUrl },
    };
  }

  const [, bucketName, filePath] = match;

  // Check if using correct bucket
  const expectedBuckets = [
    bucketName,
    bucketName_files,
    backupBucketName,
    "audio",
    "files",
    "backup-audio",
  ];
  const isValidBucket = expectedBuckets.includes(bucketName);

  if (!isValidBucket) {
    return {
      isValid: false,
      error: `Audio stored in wrong bucket '${bucketName}'. Expected buckets: ${expectedBuckets.join(
        ", "
      )}`,
      details: {
        bucket: bucketName,
        filePath,
        expectedBuckets,
        url: audioUrl,
      },
    };
  }

  // Check for common file path issues
  const fileName = filePath.split("/").pop();
  const hasValidExtension = STORAGE_AUDIO_EXT_PATTERN.test(fileName);

  if (!hasValidExtension) {
    return {
      isValid: false,
      error: `Invalid audio file extension for '${fileName}'. Expected: webm, mp3, wav, m4a, aac, ogg, mp4`,
      details: {
        bucket: bucketName,
        filePath,
        fileName,
        url: audioUrl,
      },
    };
  }

  return {
    isValid: true,
    error: null,
    details: {
      type: "supabase_storage",
      bucket: bucketName,
      filePath,
      fileName,
      url: audioUrl,
    },
  };
};

/**
 * Creates a proper Supabase storage URL for audio files
 * @param {string} bucketName - The bucket name
 * @param {string} filePath - The file path within the bucket
 * @returns {string} - Proper Supabase storage URL
 */
export const createSupabaseStorageUrl = (bucketName = "files", filePath) => {
  if (!filePath) {
    throw new Error("File path is required");
  }

  return `${supabaseUrl}/storage/v1/object/public/${bucketName}/${filePath}`;
};

/**
 * Extracts audio file metadata from URL
 * @param {string} audioUrl - The audio URL
 * @returns {Object} - Audio metadata
 */
export const extractAudioMetadata = (audioUrl) => {
  const validation = validateSupabaseStorageUrl(audioUrl);

  if (!validation.isValid) {
    return {
      isValid: false,
      error: validation.error,
      metadata: null,
    };
  }

  const details = validation.details;
  const fileName = details.fileName;

  // If no fileName (e.g., external_url type), return basic metadata
  if (!fileName) {
    return {
      isValid: true,
      error: null,
      metadata: {
        fileName: null,
        extension: null,
        nameWithoutExt: null,
        recordedDate: null,
        bucket: details.bucket || null,
        filePath: details.filePath || null,
        url: audioUrl,
        type: details.type || "unknown",
      },
    };
  }

  // Extract file info
  const nameWithoutExt = fileName.replace(/\.[^/.]+$/, "");
  const extension = fileName.split(".").pop().toLowerCase();

  // Try to extract date from filename (common pattern: _recording_YYYY-MM-DD_HH-MM-SS)
  const dateMatch = nameWithoutExt.match(
    /_recording_(\d{4}-\d{2}-\d{2})_(\d{2}-\d{2}-\d{2})/
  );
  const recordedDate = dateMatch
    ? `${dateMatch[1]} ${dateMatch[2].replace(/-/g, ":")}`
    : null;

  return {
    isValid: true,
    error: null,
    metadata: {
      fileName,
      extension,
      nameWithoutExt,
      recordedDate,
      bucket: details.bucket,
      filePath: details.filePath,
      url: audioUrl,
      type: details.type,
    },
  };
};

/**
 * Validates audio file accessibility with a HEAD request
 * @param {string} audioUrl - The audio URL to check
 * @returns {Promise<Object>} - Accessibility check result
 */
export const checkAudioAccessibility = async (audioUrl) => {
  if (!audioUrl) {
    return {
      isAccessible: false,
      status: null,
      error: "No URL provided",
    };
  }

  // The buckets are private, so /object/public/* answers 400 for every object,
  // present or not. Sign first so the answer describes the FILE and not the route.
  // toSignedUrl returns anything that is not a public storage URL unchanged, so an
  // already-signed URL is checked as-is.
  let checkUrl = audioUrl;
  try {
    checkUrl = (await toSignedUrl(audioUrl)) || audioUrl;
  } catch {
    checkUrl = audioUrl;
  }

  // A real HEAD, including on signed URLs: those answer 200 (verified against the
  // live storage server). This used to short-circuit to a canned "accessible",
  // which reported success for files that were not there.
  try {
    const response = await fetch(checkUrl, {
      method: "HEAD",
      mode: "cors",
    });

    const isAccessible = response.ok;
    const contentType = response.headers.get("content-type");
    const contentLength = response.headers.get("content-length");

    return {
      isAccessible,
      status: response.status,
      statusText: response.statusText,
      contentType,
      contentLength,
      error: isAccessible
        ? null
        : `HTTP ${response.status}: ${response.statusText}`,
    };
  } catch (error) {
    return {
      isAccessible: false,
      status: null,
      error: error.message || "Network error",
    };
  }
};

/**
 * Gets user-friendly error message based on validation and accessibility results
 * @param {string} audioUrl - The audio URL
 * @param {Object} validation - Validation result from validateSupabaseStorageUrl
 * @param {Object} accessibility - Accessibility check result from checkAudioAccessibility
 * @returns {string} - User-friendly error message
 */
export const getAudioErrorMessage = (audioUrl, validation, accessibility) => {
  if (!validation?.isValid) {
    return validation.error || "Invalid audio URL format";
  }

  if (accessibility && !accessibility.isAccessible) {
    if (accessibility.status === 404) {
      return "Audio file not found - it may have been moved or deleted";
    } else if (accessibility.status === 403) {
      return "Access denied - you don't have permission to view this audio file";
    } else if (accessibility.status === 400) {
      return "Invalid audio file - the file may be corrupted or in an unsupported format";
    } else if (accessibility.status && accessibility.status >= 500) {
      return "Server error - the audio storage service is temporarily unavailable";
    } else {
      return accessibility.error || "Unable to access audio file";
    }
  }

  return "Audio file is not available";
};

/**
 * Common audio file extensions supported by browsers
 */
export const SUPPORTED_AUDIO_FORMATS = [
  "audio/webm",
  "audio/webm;codecs=opus",
  "audio/mp4",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/wav",
  "audio/wav;codecs=1",
  "audio/ogg",
  "audio/ogg;codecs=opus",
  "audio/mpeg",
  "audio/m4a",
  "audio/aac",
];

/**
 * File extensions that map to audio formats
 */
export const AUDIO_EXTENSIONS = [
  "webm",
  "mp3",
  "wav",
  "m4a",
  "aac",
  "ogg",
  "mp4",
];

/**
 * Gets a single MediaStream that combines microphone audio and optionally system/tab audio
 * (e.g. from a shared tab or online meeting). Use for "aggregate" recording so both
 * the user's voice and other participants (or tab audio) are captured.
 *
 * When includeSystemAudio is true, the user will be prompted to share a tab or screen;
 * they should choose the tab/window where the meeting is playing and enable "Share audio"
 * so that meeting audio is captured and mixed with the microphone.
 *
 * @param {Object} options
 * @param {MediaTrackConstraints} [options.audioConstraints] - Constraints for getUserMedia (e.g. deviceId, echoCancellation)
 * @param {boolean} [options.includeSystemAudio] - If true, prompt for display/tab share with audio and mix with mic
 * @returns {Promise<{ stream: MediaStream, cleanup: () => void }>}
 */
export async function getAggregateAudioStream({
  audioConstraints = {},
  includeSystemAudio = false,
}) {
  const micStream = await navigator.mediaDevices.getUserMedia({
    audio: audioConstraints,
  });

  if (!includeSystemAudio) {
    return {
      stream: micStream,
      cleanup: () => micStream.getTracks().forEach((t) => t.stop()),
    };
  }

  let displayStream;
  try {
    displayStream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: true,
    });
  } catch (err) {
    micStream.getTracks().forEach((t) => t.stop());
    throw err;
  }

  // User must check "Also share tab audio" in the browser dialog; otherwise the stream has no audio track
  const audioTracks = displayStream.getAudioTracks();
  if (!audioTracks.length) {
    displayStream.getTracks().forEach((t) => t.stop());
    micStream.getTracks().forEach((t) => t.stop());
    const e = new Error(
      "TAB_AUDIO_REQUIRED: Please check \"Also share tab audio\" in the share dialog so meeting audio can be captured."
    );
    e.code = "TAB_AUDIO_REQUIRED";
    throw e;
  }

  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const destination = audioContext.createMediaStreamDestination();
  const micSource = audioContext.createMediaStreamSource(micStream);
  let displaySource;
  try {
    displaySource = audioContext.createMediaStreamSource(displayStream);
  } catch (mixErr) {
    displayStream.getTracks().forEach((t) => t.stop());
    micStream.getTracks().forEach((t) => t.stop());
    audioContext.close();
    const isNoAudioTrack =
      mixErr.name === "InvalidStateError" ||
      (mixErr.message && mixErr.message.includes("no audio track"));
    if (isNoAudioTrack) {
      const e = new Error(
        "TAB_AUDIO_REQUIRED: Please check \"Also share tab audio\" in the share dialog so meeting audio can be captured."
      );
      e.code = "TAB_AUDIO_REQUIRED";
      throw e;
    }
    throw mixErr;
  }
  micSource.connect(destination);
  displaySource.connect(destination);

  return {
    stream: destination.stream,
    cleanup: () => {
      displayStream.getTracks().forEach((t) => t.stop());
      micStream.getTracks().forEach((t) => t.stop());
      audioContext.close();
    },
  };
}
